import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdAttachFile, MdDeleteOutline, MdSwapHoriz } from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import OdooAutocomplete from './OdooAutocomplete';
import { fetchWithHrToken } from '../../utils/hrApiHelpers';

function todayIso() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function fileToPayload(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve({
        name: file.name,
        mimetype: file.type || 'application/octet-stream',
        data: result.includes(',') ? result.split(',', 2)[1] : result,
      });
    };
    reader.readAsDataURL(file);
  });
}

const EMPTY_FORM = {
  target_faculty_id: '',
  target_department_id: '',
  target_batch_id: '',
  previous_university: '',
  previous_university_id: '',
  previous_faculty: '',
  credits_transferred: '0',
  transfer_date: '',
  transfer_reason: '',
  approval_reference: '',
  documents_verified: false,
  notes: '',
};

function relationId(record, field) {
  const candidates = [
    record?.[field + '_id_int'],
    record?.[field + '_id'],
    record?.[field],
  ];
  for (const value of candidates) {
    const candidate = Array.isArray(value) ? value[0] : value && typeof value === 'object' ? value.id : value;
    if (candidate !== undefined && candidate !== null && candidate !== '' && candidate !== false) return candidate;
  }
  return '';
}

function relationName(record, field) {
  const candidates = [
    record?.[field + '_name'],
    record?.[field + '_id'],
    record?.[field],
  ];
  for (const value of candidates) {
    const candidate = Array.isArray(value)
      ? value[1]
      : value && typeof value === 'object'
        ? value.name || value.display_name
        : typeof value === 'string' && !/^\d+$/.test(value)
          ? value
          : '';
    if (candidate) return candidate;
  }
  return '';
}

function firstText(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '' && value !== false) || '';
}

function hydrateExternalTransfer(rawTransfer, student) {
  const transfer = rawTransfer || {};
  const record = student || {};
  const studentName = firstText(
    transfer.student_name,
    record.name,
    record.display_name,
    [record.first_name, record.middle_name, record.last_name].filter(Boolean).join(' ')
  );
  const state = firstText(transfer.state, record.state);
  return {
    ...transfer,
    student_id: transfer.student_id || record.id,
    student_name: studentName,
    admission_number: firstText(transfer.admission_number, record.admission_number, record.application_number),
    state,
    state_label: firstText(transfer.state_label, record.state_label, state),
    can_process: transfer.can_process !== undefined && transfer.can_process !== null
      ? transfer.can_process
      : state === 'draft' && !record.student_no,
    warnings: Array.isArray(transfer.warnings) ? transfer.warnings : [],
    intake_id: transfer.intake_id || relationId(record, 'admission_intake'),
    intake_name: firstText(transfer.intake_name, relationName(record, 'admission_intake'), record.intake_name),
    intake_academic_year_name: firstText(
      transfer.intake_academic_year_name,
      record.intake_academic_year_name,
      record.admission_intake_academic_year_name
    ),
    target_faculty_id: transfer.target_faculty_id || relationId(record, 'faculty'),
    target_faculty_name: firstText(transfer.target_faculty_name, relationName(record, 'faculty')),
    target_department_id: transfer.target_department_id || relationId(record, 'department'),
    target_department_name: firstText(transfer.target_department_name, relationName(record, 'department')),
    target_batch_id: transfer.target_batch_id || relationId(record, 'batch'),
    target_batch_name: firstText(transfer.target_batch_name, relationName(record, 'batch')),
  };
}

export default function ExternalTransferModal({
  open,
  studentId,
  ensureHrToken,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('previous');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState('Select a batch with a code');
  const [batchYear, setBatchYear] = useState('');
  const [form, setForm] = useState({ ...EMPTY_FORM, transfer_date: todayIso() });
  const bodyRef = useRef(null);

  const setField = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const load = useCallback(async () => {
    if (!open || !studentId) return;
    setLoading(true);
    setBusy(false);
    setConfirmOpen(false);
    setActiveTab('previous');
    setError(null);
    setFiles([]);
    setDepartments([]);
    setBatches([]);

    const [infoResult, studentResult] = await Promise.all([
      fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getStudentExternalTransfer(Number(studentId), token)
      ),
      fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getStudent({ student_id: Number(studentId), include_image: false }, token)
      ),
    ]);
    const transferPayload = infoResult.data || {};
    const studentPayload = studentResult.data || {};
    const rawTransfer = transferPayload.external_transfer || transferPayload;
    const student = studentPayload.student || studentPayload.record || studentPayload;
    const transfer = hydrateExternalTransfer(rawTransfer, student);
    if (infoResult.error) {
      transfer.can_process = false;
      setError(infoResult.error);
    } else if (studentResult.error) {
      setError(studentResult.error);
    }
    setInfo(transfer);
    setPreview(transfer?.new_student_no_preview || 'Select a batch with a code');
    setBatchYear(transfer?.target_academic_year_name || '');
    setForm({
      ...EMPTY_FORM,
      target_faculty_id: transfer?.target_faculty_id || '',
      target_department_id: transfer?.target_department_id || '',
      target_batch_id: transfer?.target_batch_id || '',
      transfer_date: String(transfer?.transfer_date || todayIso()).slice(0, 10),
    });

    const facultyResult = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getFaculties(token)
    );
    if (facultyResult.error) setError(facultyResult.error);
    setFaculties(facultyResult.data?.faculties || facultyResult.data?.records || []);
    setLoading(false);
  }, [ensureHrToken, open, studentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = window.requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [loading, open, studentId]);

  useEffect(() => {
    if (!open || !form.target_faculty_id) {
      setDepartments([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getDepartments(form.target_faculty_id, token)
      );
      if (!cancelled) {
        if (result.error) setError(result.error);
        setDepartments(result.data?.departments || result.data?.records || []);
      }
    })();
    return () => { cancelled = true; };
  }, [ensureHrToken, open, form.target_faculty_id]);

  useEffect(() => {
    if (!open || !form.target_department_id) {
      setBatches([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const result = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.getBatches(form.target_department_id, token, {
          faculty_id: form.target_faculty_id || undefined,
        })
      );
      if (!cancelled) {
        if (result.error) setError(result.error);
        setBatches(result.data?.batches || result.data?.records || []);
      }
    })();
    return () => { cancelled = true; };
  }, [ensureHrToken, open, form.target_department_id, form.target_faculty_id]);

  useEffect(() => {
    if (!open || !form.target_batch_id) {
      setPreview('Select a batch with a code');
      setBatchYear('');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const result = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.previewStudentExternalTransfer(
          { target_batch_id: Number(form.target_batch_id) }, token
        )
      );
      if (!cancelled) {
        if (result.error) setError(result.error);
        else {
          setPreview(result.data?.new_student_no_preview || 'Select a batch with a code');
          setBatchYear(result.data?.target_academic_year_name || '');
        }
      }
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ensureHrToken, open, form.target_batch_id]);

  if (!open) return null;

  const previousComplete = [
    form.previous_university,
    form.previous_university_id,
    form.previous_faculty,
  ].every((value) => Boolean(String(value || '').trim()));
  const approvalComplete = [
    form.transfer_reason,
    form.approval_reference,
  ].every((value) => Boolean(String(value || '').trim()));
  const placementComplete =
    Boolean(form.target_faculty_id) &&
    Boolean(form.target_department_id) &&
    Boolean(form.target_batch_id);
  const formReady =
    info?.can_process !== false &&
    placementComplete &&
    previousComplete &&
    approvalComplete &&
    Number(form.credits_transferred) >= 0 &&
    form.documents_verified &&
    files.length > 0;

  const openConfirm = () => {
    if (busy) return;
    if (!formReady) {
      if (placementComplete && !previousComplete) setActiveTab('previous');
      else if (placementComplete && (!approvalComplete || !form.documents_verified)) setActiveTab('approval');
      else if (placementComplete && files.length === 0) setActiveTab('papers');
      setError('Complete all required transfer fields, verify the documents, and upload at least one scanned paper.');
      return;
    }
    setError(null);
    setConfirmOpen(true);
  };

  const runTransfer = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const documents = await Promise.all(files.map(fileToPayload));
      const result = await fetchWithHrToken(ensureHrToken, (token) =>
        admissionsAPI.confirmStudentExternalTransfer({
          student_id: Number(studentId),
          target_faculty_id: Number(form.target_faculty_id),
          target_department_id: Number(form.target_department_id),
          target_batch_id: Number(form.target_batch_id),
          previous_university: form.previous_university.trim(),
          previous_university_id: form.previous_university_id.trim(),
          previous_faculty: form.previous_faculty.trim(),
          credits_transferred: Number(form.credits_transferred),
          transfer_date: form.transfer_date,
          transfer_reason: form.transfer_reason.trim(),
          approval_reference: form.approval_reference.trim(),
          documents_verified: form.documents_verified,
          documents,
          notes: form.notes.trim(),
        }, token)
      );
      if (result.error) {
        setConfirmOpen(false);
        setError(result.error);
        return;
      }
      setConfirmOpen(false);
      onSuccess?.(result.data);
      onClose?.();
    } catch (requestError) {
      setConfirmOpen(false);
      setError(requestError?.message || 'Could not process the external transfer.');
    } finally {
      setBusy(false);
    }
  };

  const differentAcademicYear =
    info?.intake_academic_year_name &&
    batchYear &&
    info.intake_academic_year_name !== batchYear;

  return (
    <div className="ea-odoo-modal-backdrop" role="presentation" onClick={() => !busy && onClose?.()}>
      <div className="ea-odoo-modal ea-transfer-modal ea-external-transfer-modal" role="dialog" aria-modal="true" aria-labelledby="ea-external-transfer-title" onClick={(event) => event.stopPropagation()}>
        <header className="ea-odoo-modal__header">
          <h2 id="ea-external-transfer-title">Process External Transfer</h2>
          <button type="button" className="ea-odoo-modal__close" aria-label="Close" disabled={busy} onClick={onClose}>×</button>
        </header>

        <div className="ea-odoo-modal__body ea-transfer-modal__body ea-external-transfer-modal__body" ref={bodyRef}>
          {error ? <div className="ea-odoo-alert ea-odoo-alert--error ea-odoo-ext-error" role="alert">{error}</div> : null}
          {loading ? <p className="ea-odoo-muted">Loading external-transfer details…</p> : (
            <div className="ea-odoo-ext-sheet">
              <div className="ea-odoo-ext-title">
                <MdSwapHoriz size={22} aria-hidden />
                <div>
                  <h1>External Transfer Admission</h1>
                  <p>Place an approved student transferring from another university into the appropriate BU batch.</p>
                </div>
              </div>

              {(info?.warnings || []).map((warning) => <div key={warning} className="ea-odoo-alert ea-odoo-alert--warning" role="status">{warning}</div>)}

              <div className="ea-odoo-ext-top-grid">
                <section className="ea-odoo-ext-group">
                  <h3>Registration</h3>
                  <dl className="ea-odoo-ext-readonly">
                    <div><dt>Registration</dt><dd className="ea-odoo-ext-link">{info?.student_name || '—'}</dd></div>
                    <div><dt>Admission Number</dt><dd>{info?.admission_number || '—'}</dd></div>
                    <div><dt>Admission Intake</dt><dd className="ea-odoo-ext-link">{info?.intake_name || '—'}</dd></div>
                    <div><dt>Intake Academic Year</dt><dd className="ea-odoo-ext-link">{info?.intake_academic_year_name || '—'}</dd></div>
                  </dl>
                </section>

                <section className="ea-odoo-ext-group">
                  <h3>Approved BU Placement</h3>
                  <div className="ea-odoo-ext-form-row">
                    <label>Faculty</label>
                    <OdooAutocomplete className="ea-odoo-ext-ac" value={form.target_faculty_id} options={faculties} placeholder="" aria-label="Faculty" onChange={(value) => {
                      setField('target_faculty_id', value);
                      setField('target_department_id', '');
                      setField('target_batch_id', '');
                    }} />
                  </div>
                  <div className="ea-odoo-ext-form-row">
                    <label>Department</label>
                    <OdooAutocomplete className="ea-odoo-ext-ac" value={form.target_department_id} options={departments} disabled={!form.target_faculty_id} placeholder="" aria-label="Department" onChange={(value) => {
                      setField('target_department_id', value);
                      setField('target_batch_id', '');
                    }} />
                  </div>
                  <div className="ea-odoo-ext-form-row">
                    <label>Join Batch</label>
                    <OdooAutocomplete className="ea-odoo-ext-ac" value={form.target_batch_id} options={batches} disabled={!form.target_department_id} placeholder="" aria-label="Join Batch" onChange={(value) => setField('target_batch_id', value)} />
                  </div>
                  <div className="ea-odoo-ext-form-row"><label>Batch Academic Year</label><div className="ea-odoo-ext-static ea-odoo-ext-link">{batchYear || '—'}</div></div>
                  <div className="ea-odoo-ext-form-row"><label>BU Student ID</label><div className="ea-odoo-ext-static">{preview}</div></div>
                </section>
              </div>

              <div className={`ea-odoo-ext-notice${differentAcademicYear ? ' ea-odoo-ext-notice--warning' : ''}`} role="status">
                {differentAcademicYear ? (
                  <><strong>External-transfer exception:</strong> this registration intake is in <strong>{info.intake_academic_year_name}</strong>, while the approved destination batch is in <strong>{batchYear}</strong>. This exception applies only to this documented external transfer.</>
                ) : 'The student will be admitted as an external transfer and assigned a new BU Student ID.'}
              </div>

              <div className="ea-odoo-ext-notebook">
                <div className="ea-odoo-ext-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={activeTab === 'previous'} className={activeTab === 'previous' ? 'active' : ''} onClick={() => setActiveTab('previous')}>Previous University</button>
                  <button type="button" role="tab" aria-selected={activeTab === 'approval'} className={activeTab === 'approval' ? 'active' : ''} onClick={() => setActiveTab('approval')}>Approval</button>
                  <button type="button" role="tab" aria-selected={activeTab === 'papers'} className={activeTab === 'papers' ? 'active' : ''} onClick={() => setActiveTab('papers')}>Scanned Papers</button>
                </div>

                <div className="ea-odoo-ext-tab-panel">
                  {activeTab === 'previous' ? (
                    <div className="ea-odoo-ext-tab-grid">
                      <div>
                        <label className="ea-odoo-ext-input-row"><span>Previous University</span><input value={form.previous_university} onChange={(event) => setField('previous_university', event.target.value)} /></label>
                        <label className="ea-odoo-ext-input-row"><span>Previous University Student ID</span><input value={form.previous_university_id} onChange={(event) => setField('previous_university_id', event.target.value)} /></label>
                      </div>
                      <div>
                        <label className="ea-odoo-ext-input-row"><span>Previous Programme / Faculty</span><input value={form.previous_faculty} onChange={(event) => setField('previous_faculty', event.target.value)} /></label>
                        <label className="ea-odoo-ext-input-row"><span>Credits Accepted</span><input type="number" min="0" step="1" value={form.credits_transferred} onChange={(event) => setField('credits_transferred', event.target.value)} /></label>
                      </div>
                    </div>
                  ) : null}

                  {activeTab === 'approval' ? (
                    <div className="ea-odoo-ext-approval">
                      <label className="ea-odoo-ext-input-row ea-odoo-ext-input-row--narrow"><span>Transfer Date</span><input type="date" value={form.transfer_date} onChange={(event) => setField('transfer_date', event.target.value)} /></label>
                      <label className="ea-odoo-ext-input-row ea-odoo-ext-input-row--narrow"><span>Approval Reference <sup>?</sup></span><input value={form.approval_reference} onChange={(event) => setField('approval_reference', event.target.value)} placeholder="e.g. AC/2026/041" /></label>
                      <label className="ea-odoo-ext-checkbox-row"><span>Source documents verified <sup>?</sup></span><input type="checkbox" checked={form.documents_verified} onChange={(event) => setField('documents_verified', event.target.checked)} /></label>
                      <textarea className="ea-odoo-ext-wide-text" rows={3} value={form.transfer_reason} onChange={(event) => setField('transfer_reason', event.target.value)} placeholder="Reason for accepting this transfer" />
                      <textarea className="ea-odoo-ext-wide-text" rows={3} value={form.notes} onChange={(event) => setField('notes', event.target.value)} placeholder="Optional placement or credit-transfer notes" />
                    </div>
                  ) : null}

                  {activeTab === 'papers' ? (
                    <div className="ea-odoo-ext-papers">
                      <div className="ea-odoo-ext-papers__help">Upload the approved transfer letter, transcript, and any other supporting papers. These files will be retained on the admitted student record.</div>
                      <div className="ea-odoo-ext-papers__files">
                        <label className="ea-odoo-ext-upload"><MdAttachFile size={18} aria-hidden /><strong>Scanned transfer papers</strong><input type="file" multiple accept=".pdf,image/*,.doc,.docx" onChange={(event) => {
                          const selected = Array.from(event.target.files || []);
                          setFiles((current) => [...current, ...selected].slice(0, 10));
                          event.target.value = '';
                        }} /></label>
                        <div className="ea-odoo-ext-file-list">
                          {files.map((file, index) => <div className="ea-odoo-ext-file" key={`${file.name}-${file.lastModified}-${index}`}><span>{file.name}</span><button type="button" aria-label={`Remove ${file.name}`} onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))}><MdDeleteOutline size={17} aria-hidden /></button></div>)}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        <footer className="ea-odoo-modal__footer">
          <button type="button" className="ea-odoo-btn ea-odoo-btn--danger" disabled={busy || loading || info?.can_process === false} onClick={openConfirm}>{busy ? 'Processing…' : 'Approve Transfer & Admit'}</button>
          <button type="button" className="ea-odoo-btn ea-odoo-btn--ghost" disabled={busy} onClick={onClose}>Cancel</button>
        </footer>
      </div>

      {confirmOpen ? (
        <div className="ea-odoo-modal-backdrop ea-odoo-modal-backdrop--nested" role="presentation" onClick={() => !busy && setConfirmOpen(false)}>
          <div className="ea-odoo-modal ea-odoo-modal--confirm" role="dialog" aria-modal="true" aria-labelledby="ea-external-confirm-title" onClick={(event) => event.stopPropagation()}>
            <div className="ea-odoo-modal__header"><h2 id="ea-external-confirm-title">Approve External Transfer?</h2><button type="button" className="ea-odoo-modal__close" aria-label="Close" disabled={busy} onClick={() => setConfirmOpen(false)}>×</button></div>
            <div className="ea-odoo-modal__body"><p>This will admit <strong>{info?.student_name}</strong> as an external transfer and generate a permanent BU Student ID.</p></div>
            <div className="ea-odoo-modal__footer"><button type="button" className="ea-odoo-btn ea-odoo-btn--danger" disabled={busy} onClick={runTransfer}>{busy ? 'Processing…' : 'Approve & Admit'}</button><button type="button" className="ea-odoo-btn ea-odoo-btn--ghost" disabled={busy} onClick={() => setConfirmOpen(false)}>Cancel</button></div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
